import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'

import TabStore from 'stores/tab/TabStore'

import Tooltip from 'components/tooltip/Tooltip'
import Input from 'components/input/Input'
import Checkbox from 'components/checkbox/Checkbox'

import styles from './fieldFilter.scss'
import commonStyles from './common.scss'

interface IProps {}

interface IState {
  isOpen: boolean
  searchValue: string
}

class FieldFilter extends React.PureComponent<IProps, IState> {
  private button: React.RefObject<HTMLDivElement> = React.createRef()

  state: IState = {
    isOpen: false,
    searchValue: '',
  }

  handleToggle = () => {
    this.setState(state => ({ isOpen: !state.isOpen }))
  }

  handleSelect = async (fieldId: string) => {
    TabStore.activeTab?.update({ preview: false })

    const script = TabStore.activeTab?.session!.script!

    let newFieldIds
    if (script.fieldIds.includes(fieldId)) {
      newFieldIds = script.fieldIds.filter(i => i !== fieldId)
    } else {
      newFieldIds = script.fieldIds.concat(fieldId)
    }

    script.update({ frozen: false, fieldIds: newFieldIds })
  }

  handleSearch = (searchValue: string) => {
    this.setState({ searchValue })
  }

  handleSelectAll = async () => {
    TabStore.activeTab?.update({ preview: false })

    const script = TabStore.activeTab?.session!.script!

    const newFieldIds = script.fieldIds.length > 0 ? [] : script.model.fieldIds

    script.update({ frozen: false, fieldIds: newFieldIds })
  }

  render() {
    const { isOpen, searchValue } = this.state
    const { model, fieldIds } = TabStore.activeTab!.session!.script

    const fields = model.fields.filter(f =>
      f.name.toLowerCase().includes(searchValue.toLowerCase())
    )
    const activeFieldIds = model.fieldIds.filter(fId => fieldIds.includes(fId))

    return (
      <div className={commonStyles.container}>
        <div
          data-cy="field-filter"
          ref={this.button}
          className={cn(commonStyles.pill, {
            [commonStyles.open]: isOpen,
            [commonStyles.exists]: activeFieldIds.length < model.fields.length,
          })}
          onClick={this.handleToggle}
        >
          <span className={commonStyles.label}>Fields</span>
          <span>
            {activeFieldIds.length === model.fields.length
              ? 'All'
              : activeFieldIds.length}
          </span>
        </div>
        {isOpen && (
          <Tooltip
            className={styles.tooltip}
            target={this.button}
            onClickOutside={this.handleToggle}
          >
            <>
              <Checkbox
                className={styles.checkbox}
                checked={activeFieldIds.length > 0}
                indeterminate={activeFieldIds.length < model.fields.length}
                onChange={() => this.handleSelectAll()}
              />
              <Input
                type="text"
                className={styles.search}
                placeholder="Search"
                value={searchValue}
                onChange={this.handleSearch}
              />
              {fields.map(f => {
                const active = activeFieldIds.includes(f.id)
                return (
                  <React.Fragment key={f.id}>
                    <Checkbox
                      className={styles.checkbox}
                      checked={active}
                      onChange={() => this.handleSelect(f.id)}
                    />
                    <div
                      data-cy="filter-option"
                      className={styles.name}
                      onClick={() => this.handleSelect(f.id)}
                    >
                      {f.name}
                    </div>
                    <div
                      className={styles.type}
                      onClick={() => this.handleSelect(f.id)}
                    >
                      {f.typeAsLabel}
                    </div>
                  </React.Fragment>
                )
              })}
            </>
          </Tooltip>
        )}
      </div>
    )
  }
}

export default observer(FieldFilter)
